/**
 * OEM generation source register.
 *
 * This research catalog is deliberately separate from the solver-applicable
 * `GENERATION_EQUIPMENT_CATALOG`.  A product may be useful for equipment
 * screening or a preliminary spatial test fit without having enough data to
 * become an immutable simulation duty envelope.
 */
export const OEM_GENERATION_SOURCE_REGISTER_SCHEMA_VERSION = 2 as const;

export type OemGenerationTechnology =
  | 'industrial-gas-turbine'
  | 'aeroderivative-gas-turbine'
  | 'microturbine'
  | 'spark-ignited-gas-engine'
  | 'compression-ignition-engine'
  | 'dual-fuel-engine';

export type OemGenerationPackageScope =
  | 'engine-only'
  | 'generator-set'
  | 'packaged-module'
  | 'power-island'
  | 'combined-heat-and-power-package';

export type OemGenerationDuty =
  | 'continuous'
  | 'prime'
  | 'standby'
  | 'mission-critical'
  | 'data-center-continuous'
  | 'iso-rating'
  | 'not-stated';

export type OemGenerationRatingScope =
  | 'engine-shaft'
  | 'generator-terminals'
  | 'package-gross'
  | 'package-net'
  | 'plant-gross'
  | 'plant-net';

export type OemHeatingValueBasis = 'LHV' | 'HHV' | 'not-stated';

export type OemSourceAccess =
  | 'public'
  | 'registration-required'
  | 'proposal-required';

export type OemSourceKind =
  | 'product-page'
  | 'datasheet'
  | 'product-guide'
  | 'portfolio'
  | 'sizing-tool'
  | 'application-manual';

export interface OemGenerationSource {
  id: string;
  publisher: string;
  title: string;
  url: string;
  retrievedAt: string;
  kind: OemSourceKind;
  access: OemSourceAccess;
  revision?: string;
  publishedAt?: string;
  locator?: string;
  notes?: string;
}

export interface OemHeatRate {
  valueKjPerKwh: number;
  heatingValueBasis: OemHeatingValueBasis;
}

export interface OemGenerationPerformanceCase {
  id: string;
  label: string;
  frequencyHz: readonly (50 | 60)[];
  duty: OemGenerationDuty;
  fuel: string;
  electricalOutputKw: number;
  ratingScope: OemGenerationRatingScope;
  powerFactor: number | null;
  electricalEfficiencyPct: number | null;
  heatRate: OemHeatRate | null;
  fuelInputKw: number | null;
  exhaustMassFlowKgS: number | null;
  exhaustTemperatureC: number | null;
  steamProductionKgPerHour: number | null;
  minimumLoadPct: number | null;
  startTimeMinutes: number | null;
  rampRateMwPerMinute: number | null;
  referenceConditions: string;
  sourceIds: readonly string[];
  notes?: string;
}

export interface OemGenerationPartLoadPoint {
  performanceCaseId: string;
  loadPct: number;
  electricalOutputKw: number | null;
  electricalEfficiencyPct: number | null;
  heatRate: OemHeatRate | null;
  fuelInputKw: number | null;
  sourceIds: readonly string[];
  notes?: string;
}

/** Split recoverable-heat data at one declared performance case. */
export interface OemGenerationHeatRecoveryCase {
  performanceCaseId: string;
  jacketWaterKw: number | null;
  firstStageChargeAirKw: number | null;
  secondStageChargeAirKw: number | null;
  lubeOilKw: number | null;
  exhaustKw: number | null;
  totalThermalOutputKw: number | null;
  sourceIds: readonly string[];
  notes?: string;
}

export interface OemGeneratorReactances {
  xdPu: number | null;
  xPrimeDPu: number | null;
  xDoublePrimeDPu: number | null;
  xqPu: number | null;
  xDoublePrimeQPu: number | null;
  negativeSequencePu: number | null;
  zeroSequencePu: number | null;
  leakagePu: number | null;
  baseMva: number;
}

export interface OemGeneratorTimeConstants {
  tPrimeDoSeconds: number | null;
  tPrimeDSeconds: number | null;
  tDoublePrimeDSeconds: number | null;
  tDoublePrimeQSeconds: number | null;
  armatureSeconds: number | null;
}

export interface OemGenerationElectricalData {
  alternatorManufacturer: string | null;
  alternatorModel: string | null;
  ratedKva: number | null;
  nominalVoltagesKv: readonly number[];
  ratedPowerFactor: number | null;
  motorStartingKva: number | null;
  rotorInertiaKgM2: number | null;
  reactances: OemGeneratorReactances | null;
  timeConstants: OemGeneratorTimeConstants | null;
  shortCircuitDecrementCurvePublished: boolean;
  avrModelPublished: boolean;
  governorModelPublished: boolean;
  validatedRmsModelPublished: boolean;
  validatedEmtModelPublished: boolean;
  sourceIds: readonly string[];
  notes?: string;
}

export type OemPhysicalReferenceScope =
  | 'engine-only'
  | 'generator-set-skid'
  | 'transport-envelope'
  | 'packaged-module'
  | 'enclosure'
  | 'typical-plant-layout'
  | 'installed-operating-envelope';

export type OemPhysicalIncludedSystem =
  | 'prime-mover'
  | 'generator'
  | 'gearbox'
  | 'controls'
  | 'acoustic-enclosure'
  | 'air-inlet'
  | 'exhaust'
  | 'fuel-conditioning'
  | 'cooling'
  | 'heat-recovery'
  | 'switchgear'
  | 'transformer'
  | 'balance-of-plant';

export type OemPhysicalConfigurationScope =
  | 'exact-product'
  | 'product-family'
  | 'project-example';

export type OemPhysicalCoverageScope =
  | 'per-product'
  | 'catalog-simulation-block'
  | 'whole-plant';

/**
 * A source drawing or dimensional table.  `lengthM × widthM` is never assumed
 * to be an installed plant footprint unless the source says so explicitly.
 */
export interface OemPhysicalReference {
  id: string;
  label: string;
  scope: OemPhysicalReferenceScope;
  lengthM: number;
  widthM: number;
  heightM: number | null;
  massTonnes: number | null;
  configurationScope: OemPhysicalConfigurationScope;
  coverageScope: OemPhysicalCoverageScope;
  quantity: number;
  includes: readonly OemPhysicalIncludedSystem[];
  maintenanceClearanceIncluded: boolean | null;
  fireAccessIncluded: boolean | null;
  sourceIds: readonly string[];
  limitation: string;
}

/** Minimum OEM-published clearance around one physical reference. */
export interface OemPhysicalClearance {
  physicalReferenceId: string;
  frontM: number;
  rearM: number;
  leftM: number;
  rightM: number;
  topM: number | null;
  sourceIds: readonly string[];
  limitation: string;
}

export type OemAdditionalTestFitReservation =
  | 'maintenance-access'
  | 'fire-access'
  | 'air-inlet-and-exhaust'
  | 'fuel-conditioning'
  | 'cooling'
  | 'heat-recovery'
  | 'transformer-and-switchgear'
  | 'acoustic-treatment'
  | 'roads-and-crane-access'
  | 'stormwater-and-grading'
  | 'construction-laydown';

export interface OemGenerationProduct {
  schemaVersion: typeof OEM_GENERATION_SOURCE_REGISTER_SCHEMA_VERSION;
  id: string;
  manufacturer: string;
  model: string;
  variant: string;
  technology: OemGenerationTechnology;
  packageScope: OemGenerationPackageScope;
  productStatus: 'current' | 'legacy-current-support' | 'status-unconfirmed';
  fuels: readonly string[];
  identitySourceIds: readonly string[];
  preferredPerformanceCaseId: string;
  performanceCases: readonly OemGenerationPerformanceCase[];
  partLoadPoints: readonly OemGenerationPartLoadPoint[];
  heatRecoveryCases: readonly OemGenerationHeatRecoveryCase[];
  electrical: OemGenerationElectricalData;
  physicalReferences: readonly OemPhysicalReference[];
  clearances: readonly OemPhysicalClearance[];
  additionalTestFitReservations: readonly OemAdditionalTestFitReservation[];
  /** Link only when a separately governed solver envelope already exists. */
  simulationCatalogId: string | null;
  dataGaps: readonly string[];
  notes?: string;
}

export interface OemElectricalMachineRatingCase {
  id: string;
  label: string;
  ratedKva: number;
  ratedVoltageKv: number;
  frequencyHz: 50 | 60;
  phases: 3;
  ratedPowerFactor: number | null;
  poles: number;
  ratedSpeedRpm: number;
  efficiencyPct: number | null;
  windingConnection: string | null;
  enclosureProtection: string | null;
  coolingMethod: string | null;
  insulationClass: string | null;
  temperatureRiseClass: string | null;
  sourceIds: readonly string[];
  notes?: string;
}

/**
 * Rating-specific electrical study data. Parameters stay tied to the exact
 * kVA / voltage / frequency base and are never promoted from a family range.
 */
export interface OemElectricalMachineStudyCase {
  ratingCaseId: string;
  parameterBasis: 'saturated' | 'unsaturated' | 'mixed' | 'not-stated';
  reactances: OemGeneratorReactances | null;
  timeConstants: OemGeneratorTimeConstants | null;
  statorResistancePu: number | null;
  shortCircuitRatio: number | null;
  rotorInertiaKgM2: number | null;
  inertiaConstantSeconds: number | null;
  shortCircuitDecrementCurvePublished: boolean;
  neutralGroundingDataPublished: boolean;
  avrModelPublished: boolean;
  validatedRmsModelPublished: boolean;
  validatedEmtModelPublished: boolean;
  sourceIds: readonly string[];
  notes?: string;
}

/** Standalone alternator record for BOM selection and electrical studies. */
export interface OemElectricalMachineProduct {
  schemaVersion: typeof OEM_GENERATION_SOURCE_REGISTER_SCHEMA_VERSION;
  id: string;
  manufacturer: string;
  family: string;
  model: string;
  variant: string;
  productStatus: 'current' | 'legacy-current-support' | 'status-unconfirmed';
  identitySourceIds: readonly string[];
  preferredRatingCaseId: string;
  ratingCases: readonly OemElectricalMachineRatingCase[];
  studyCases: readonly OemElectricalMachineStudyCase[];
  physicalReferences: readonly OemPhysicalReference[];
  dataGaps: readonly string[];
  notes?: string;
}

export interface OemGenerationSourceRegister {
  schemaVersion: typeof OEM_GENERATION_SOURCE_REGISTER_SCHEMA_VERSION;
  compiledAt: string;
  sources: readonly OemGenerationSource[];
  products: readonly OemGenerationProduct[];
  electricalMachines: readonly OemElectricalMachineProduct[];
}

export type OemFootprintAuthority =
  | 'project-ready-installed-envelope'
  | 'preliminary-plant-layout'
  | 'preliminary-clearance-envelope'
  | 'equipment-reference-only'
  | 'missing';

export interface OemFootprintAssessment {
  authority: OemFootprintAuthority;
  physicalReferenceId: string | null;
  lengthM: number | null;
  widthM: number | null;
  areaM2: number | null;
  maintenanceClearanceIncluded: boolean;
  fireAccessIncluded: boolean;
  usableAsProjectInstalledReservation: boolean;
  warnings: readonly string[];
}

export interface OemGenerationCatalogCoverage {
  productCount: number;
  manufacturerCount: number;
  gasTurbineCount: number;
  reciprocatingCount: number;
  withPartLoadData: number;
  withPackageDimensions: number;
  withPreliminaryOrBetterFootprint: number;
  withProjectReadyInstalledFootprint: number;
  linkedSimulationEnvelopeCount: number;
  electricalMachineCount: number;
  electricalMachineManufacturerCount: number;
}
